import { useEffect, useState } from 'react';

export type Theme = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'downsample-designer:theme';
const ORDER: Theme[] = ['system', 'light', 'dark'];
const LABEL: Record<Theme, string> = { system: 'System', light: 'Light', dark: 'Dark' };
const ICON: Record<Theme, string> = { system: '◐', light: '☀', dark: '☾' };

function read(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private browsing modes can throw on access; the system default is fine.
  }
  return 'system';
}

/**
 * Cycles system → light → dark. `system` removes the attribute entirely so the
 * stylesheet falls back to `prefers-color-scheme`; the explicit values set
 * `data-theme`, which overrides the media query in both directions.
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(read);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Persistence is a nicety, not a requirement.
    }
  }, [theme]);

  const next = ORDER[(ORDER.indexOf(theme) + 1) % ORDER.length];

  return (
    <button
      type="button"
      data-testid="theme-toggle"
      aria-label={`Theme: ${LABEL[theme]}. Switch to ${LABEL[next]}.`}
      onClick={() => setTheme(next)}
    >
      <span aria-hidden="true">{ICON[theme]}</span> {LABEL[theme]}
    </button>
  );
}
